import React from 'react';
import { Dialog, DialogBody, DialogTitle } from './dialog';
import { Button } from './button';
import { ExclamationTriangleIcon, XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

/**
 * XmlConfirmationDialog Component
 * Shows validation warnings and parsed data preview when uploading/replacing XML files
 * User can confirm to proceed or cancel to abort
 */
export function XmlConfirmationDialog({
  isOpen,
  onConfirm,
  onCancel,
  parsedData,
  warnings = [],
  fileType, // 'fianzaXml' or 'facturaXml'
  isLoading = false,
}) {
  if (!isOpen) return null;

  const isFianza = fileType === 'fianzaXml';
  const title = isFianza ? 'Confirmar XML de Fianza' : 'Confirmar XML de Factura';

  const criticalWarnings = warnings.filter(w => w.type === 'critical');
  const nonCriticalWarnings = warnings.filter(w => w.type === 'warning');

  return (
    <Dialog open={isOpen} onClose={onCancel} size="lg">
      <DialogTitle>{title}</DialogTitle>
      <DialogBody>
        {/* Warnings Section */}
        {warnings.length > 0 && (
          <div className="mb-4 space-y-3">
            <div className="flex items-center gap-2 text-amber-700">
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span className="font-semibold">Se encontraron advertencias de validacion</span>
            </div>

            {criticalWarnings.length > 0 && (
              <div className="space-y-2">
                {criticalWarnings.map((warning, idx) => (
                  <div key={`critical-${idx}`} className="bg-red-50 border border-red-300 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-red-800 font-semibold text-sm">{warning.field}</div>
                        <div className="text-red-700 text-xs mt-1">{warning.message}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {nonCriticalWarnings.length > 0 && (
              <div className="space-y-2">
                {nonCriticalWarnings.map((warning, idx) => (
                  <div key={`warning-${idx}`} className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-yellow-800 font-semibold text-sm">{warning.field}</div>
                        <div className="text-yellow-700 text-xs mt-1">{warning.message}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Parsed Data Preview */}
        {parsedData && (
          <div className={`border rounded-lg p-4 ${isFianza ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircleIcon className={`w-5 h-5 ${isFianza ? 'text-emerald-600' : 'text-amber-600'}`} />
              <span className={`font-semibold text-sm ${isFianza ? 'text-emerald-800' : 'text-amber-800'}`}>
                Datos extraidos del XML
              </span>
            </div>

            {isFianza ? (
              // Fianza Preview
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <span className="text-gray-500">Numero de Fianza</span>
                  <div className="text-gray-800 font-semibold">{parsedData.numeroFianza || 'N/A'}</div>
                </div>
                <div>
                  <span className="text-gray-500">Monto Afianzado</span>
                  <div className="text-gray-800 font-semibold">
                    {parsedData.montoAfianzado
                      ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(parsedData.montoAfianzado)
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Tipo</span>
                  <div className="text-gray-800">{parsedData.tipo || 'N/A'}</div>
                </div>
                <div>
                  <span className="text-gray-500">Tarifa</span>
                  <div className="text-gray-800">{parsedData.tarifa?.toFixed(4)}%</div>
                </div>
                <div>
                  <span className="text-gray-500">Inicio Vigencia</span>
                  <div className="text-gray-800">
                    {parsedData.inicioVigenciaFianza
                      ? new Date(parsedData.inicioVigenciaFianza).toLocaleDateString('es-MX')
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Fin Vigencia</span>
                  <div className="text-gray-800">
                    {parsedData.finVigenciaFianza
                      ? new Date(parsedData.finVigenciaFianza).toLocaleDateString('es-MX')
                      : 'N/A'}
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Emisor</span>
                  <div className="text-gray-800">{parsedData.emisor?.nombre || 'N/A'}</div>
                </div>
                {parsedData.primas?.primaTotal && (
                  <div className="col-span-2 bg-emerald-100 rounded p-2 mt-1">
                    <span className="text-emerald-700 font-medium">Prima Total</span>
                    <div className="text-emerald-800 font-bold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(parsedData.primas.primaTotal)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Factura Preview
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <span className="text-gray-500">Serie-Folio</span>
                  <div className="text-gray-800 font-semibold">{parsedData.serie}-{parsedData.folio}</div>
                </div>
                <div>
                  <span className="text-gray-500">Fecha</span>
                  <div className="text-gray-800">
                    {parsedData.fecha
                      ? new Date(parsedData.fecha).toLocaleDateString('es-MX')
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">No. Identificacion</span>
                  <div className="text-gray-800 font-semibold">{parsedData.noIdentificacion || 'N/A'}</div>
                </div>
                <div>
                  <span className="text-gray-500">SubTotal</span>
                  <div className="text-gray-800">
                    {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(parsedData.subTotal || 0)}
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Emisor</span>
                  <div className="text-gray-800">{parsedData.emisor?.nombre || 'N/A'}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Receptor</span>
                  <div className="text-gray-800">{parsedData.receptor?.nombre || 'N/A'}</div>
                </div>
                <div className="col-span-2 bg-amber-100 rounded p-2 mt-1">
                  <span className="text-amber-700 font-medium">Total Factura</span>
                  <div className="text-amber-800 font-bold">
                    {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(parsedData.total || 0)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Confirmation Message */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            {warnings.length > 0
              ? 'A pesar de las advertencias, puedes continuar con la subida. Los datos extraidos se guardaran y actualizaran la fianza.'
              : 'Los datos extraidos se guardaran y actualizaran la fianza.'}
          </p>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <Button color="white" onClick={onCancel} disabled={isLoading}>
            Cancelar
          </Button>
          <Button color="surelia" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Procesando...' : 'Confirmar y Guardar'}
          </Button>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default XmlConfirmationDialog;
